import { Icon, Text, TextButton } from '@oneaudi/unified-web-alpha-components';
import { useEffect } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import {
	ButtonSectionStyles,
	PriceSectionStyles,
	StyledErrorContainer,
	StyledMainContainer,
} from '~/app/pages/configurator/components/conflict-dialog/components/styles/summary-and-confirmation-style';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { ErrorType } from '~/app/tracking/feld-m-types';

export function PriceSummaryAndConfirmation({
	totalPrice,
	priceChange,
	onAccept,
	onDecline,
	isLoading,
	isLoadingAccept,
	isError,
}: {
	totalPrice: string | null | undefined;
	priceChange: string;
	onAccept: () => void;
	onDecline: () => void;
	isLoading: boolean;
	isLoadingAccept: boolean; // Extra loading flag to prevent "Calculating price..." from showing up after solution has been accepted
	isError: boolean;
}) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();

	useEffect(() => {
		isError &&
			appTrackingService.trackError({
				name: ErrorType.CONFLICT,
				userMessage: i18n('ngc.errors.conflict'),
			});
	}, [isError, i18n]);

	return (
		<StyledMainContainer>
			{isError ? (
				<>
					<StyledErrorContainer>
						<Icon name="caution" size="s" />
						<Text>{i18n('ngc.errors.conflict')}</Text>
					</StyledErrorContainer>

					<div>
						<TextButton variant="primary" onClick={onDecline} size={'md'}>
							{i18n('ngc.actions.cancel')}
						</TextButton>
					</div>
				</>
			) : (
				<>
					{config.showPrices && (
						<PriceSectionStyles>
							<Text
								className="summary__price-headline"
								variant={{ type: 'copy', order: '1', style: 'bold' }}>
								{i18n('ngc.conflict.priceChange')}
							</Text>
							<Text
								className="summary__price-value"
								variant={{ type: 'copy', order: '1', style: 'bold' }}>
								{priceChange}
							</Text>

							<Text
								className="summary__price-headline"
								variant={{ type: 'copy', order: '1', style: 'normal' }}>
								{i18n('ngc.conflict.total')}
							</Text>
							<Text
								className="summary__price-value"
								variant={{ type: 'copy', order: '1', style: 'normal' }}>
								{!isLoading ? (
									(totalPrice ?? i18n('ngc.common.priceNotAvailable'))
								) : (
									<>{i18n('ngc.currency.calculatingPrice')}...</>
								)}
							</Text>
						</PriceSectionStyles>
					)}

					<ButtonSectionStyles>
						<TextButton variant="secondary" onClick={onDecline} size="md">
							{i18n('ngc.conflict.decline')}
						</TextButton>
						<TextButton
							variant="primary"
							onClick={onAccept}
							disabled={isLoading || isLoadingAccept || isError}
							isLoading={isLoading || isLoadingAccept}
							size={'md'}>
							{i18n('ngc.conflict.accept')}
						</TextButton>
					</ButtonSectionStyles>
				</>
			)}
		</StyledMainContainer>
	);
}
